import { api, API_URL } from './api';
import eventBus from '../utils/eventBus';

class AuthService {
    constructor() {
        this.refreshing = false;
        this.refreshQueue = [];
        this.refreshAttempts = 0;
        this.lastRefreshTime = 0;
        // Add deduplication for getCurrentUser calls
        this.getCurrentUserPromise = null;
    }

    async login(credentials) {
        try {
            const response = await api.post('/auth/login', credentials);
            return response.data;
        } catch (error) {
            // Preserve the original error with response data for proper error handling
            throw error;
        }
    }

    async signup(userData) {
        try {
            const response = await api.post('/auth/register', userData);
            return response.data;
        } catch (error) {
            // Preserve the original error (with response + status) so the UI can
            // branch on HTTP status the same way login does.
            throw error;
        }
    }

    async refreshToken(silent = false) {
        // Prevent multiple simultaneous refresh attempts
        if (this.refreshing) {
            return new Promise((resolve, reject) => {
                this.refreshQueue.push({ resolve, reject });
            });
        }

        // Implement backoff for repeated refresh attempts
        const now = Date.now();
        if (this.lastRefreshTime && (now - this.lastRefreshTime) < 1000) {
            this.refreshAttempts++;
            if (this.refreshAttempts > 3) {
                this.clearAuth();
                throw new Error('Too many refresh attempts. Please login again.');
            }
        } else {
            this.refreshAttempts = 0;
        }
        this.lastRefreshTime = now;

        try {
            this.refreshing = true;
            const response = await api.post('/auth/refresh');
            
            // Process queued requests
            this.refreshQueue.forEach(({ resolve }) => resolve(response.data));
            this.refreshQueue = [];
            
            return response.data;
        } catch (error) {
            console.error('[AuthService] Token refresh failed:', error);
            
            // Process queued requests with error
            this.refreshQueue.forEach(({ reject }) => reject(error));
            this.refreshQueue = [];

            if (!silent) {
                this.clearAuth();
                throw new Error('Session expired. Please login again.');
            }
            throw error;
        } finally {
            this.refreshing = false;
        }
    }

    async getCurrentUser() {
        // Deduplicate concurrent getCurrentUser calls
        if (this.getCurrentUserPromise) {
            return this.getCurrentUserPromise;
        }

        this.getCurrentUserPromise = this._getCurrentUserInternal();
        
        try {
            const result = await this.getCurrentUserPromise;
            return result;
        } finally {
            this.getCurrentUserPromise = null;
        }
    }

    async _getCurrentUserInternal() {
        try {
            const response = await api.get('/auth/me');
            return response.data;
        } catch (error) {
            // The API interceptor handles 401 by attempting token refresh and retrying.
            // If we reach here, it means either:
            // 1. The refresh failed (token expired/invalid), OR
            // 2. It wasn't a 401 error
            // 
            // Important: Do NOT call clearAuth() here - the interceptor already handles
            // the refresh flow. If the interceptor successfully refreshes and retries,
            // this catch block won't even execute (the retry resolves the promise).
            // Only clear auth if we're absolutely sure the session is invalid.
            if (error.response?.status === 401) {
                // 401 after interceptor means refresh was attempted and failed
                // The interceptor only passes through 401 if refresh failed
                throw new Error('Session expired');
            }
            throw error;
        }
    }

    clearAuth() {
        // Reset internal state
        this.refreshing = false;
        this.refreshQueue = [];
        this.refreshAttempts = 0;
        this.lastRefreshTime = 0;
        this.getCurrentUserPromise = null; // Clear any pending getCurrentUser calls
        
        eventBus.emit('auth:logout');
    }

    async logout() {
        try {
            // Ensure we have a CSRF token before logout
            await api.get('/public/csrf');
            await api.post('/auth/logout');
        } catch (error) {
            console.error('Logout error:', error);
            // Continue with clearAuth even if logout request fails
        } finally {
            this.clearAuth();
        }
    }

    async exitImpersonation() {
        try {
            await api.get('/public/csrf');
            await api.post('/auth/impersonate/exit');
        } catch (error) {
            console.error('Exit impersonation error:', error);
        } finally {
            this.clearAuth();
        }
    }

    async forgotPassword(email) {
        try {
            const response = await api.post('/auth/forgot-password', { email });
            return response.data;
        } catch (error) {
            if (error.response) {
                throw new Error(error.response.data?.message || 'Failed to send password reset email. Please try again.');
            }
            throw error;
        }
    }

    async resetPassword(token, newPassword, confirmPassword) {
        try {
            const response = await api.post('/auth/reset-password', { 
                token, 
                newPassword,
                confirmPassword
            });
            return response.data;
        } catch (error) {
            if (error.response) {
                throw new Error(error.response.data?.message || 'Failed to reset password. Please try again.');
            }
            throw error;
        }
    }

    async validateResetToken(token) {
        try {
            const response = await api.get(`/auth/reset-password/validate/${token}`);
            const data = response.data;
            
            // Check if the token is valid based on the response
            if (!data.valid) {
                throw new Error('Invalid or expired reset token.');
            }
            
            return data;
        } catch (error) {
            if (error.response) {
                throw new Error(error.response.data?.message || 'Invalid or expired reset token.');
            }
            throw error;
        }
    }

    async resendVerificationEmail() {
        try {
            const response = await api.post('/auth/resend-verification');
            return response.data;
        } catch (error) {
            if (error.response) {
                throw new Error(error.response.data?.message || 'Failed to resend verification email. Please try again.');
            }
            throw error;
        }
    }

    async verifyEmail(token) {
        try {
            const response = await api.post(`/auth/verify-email?token=${token}`);
            return response.data;
        } catch (error) {
            if (error.response) {
                throw new Error(error.response.data?.message || 'Verification failed. The link may have expired.');
            }
            throw error;
        }
    }

    async verifyTwoFactor(twoFactorToken, code, codeType) {
        try {
            const response = await api.post('/auth/2fa/challenge', {
                twoFactorToken,
                code,
                codeType
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Begin a social login by full-page redirecting to the backend start endpoint.
     * The backend will 302 to the provider, handle the callback, set HttpOnly
     * cookies, and redirect back to `returnTo` (or to /finish-signup if needed).
     */
    startSocialLogin(provider, { returnTo = '/', intent = 'LOGIN' } = {}) {
        const slug = String(provider).toLowerCase();
        const params = new URLSearchParams({ intent, returnTo });
        window.location.assign(`${API_URL}/auth/social/${slug}/start?${params.toString()}`);
    }

    async finishSocialSignup(token, { email, username } = {}) {
        const response = await api.post('/auth/social/finish-signup', { token, email, username });
        return response.data;
    }

    async listSocialAccounts() {
        const response = await api.get('/auth/social/accounts');
        return response.data;
    }

    async unlinkSocialAccount(provider) {
        await api.delete(`/auth/social/accounts/${String(provider).toLowerCase()}`);
    }
}

export const authService = new AuthService();